<template>
  <base-material-card
    icon="mdi-gavel"
    title="Offense Rules"
    class="px-5 py-3"
  >
  <v-btn
      color="green"
      dark
      large
      absolute
      top
      right
      fab
 :disabled="isNewOffenseRowOpen"
            @click="addNewOffenseRow"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
    <v-data-table
      :headers="headers"
      :items="offenseTypes"
      :expanded.sync="expanded"
      show-expand
      item-key="id"
    >
      <!-- Top toolbar template -->
      <!-- <template v-slot:top>
        <v-toolbar flat>
          <v-btn
            color="primary"
            dark
            :disabled="isNewOffenseRowOpen"
            @click="addNewOffenseRow"
          >
            Add Offense Type
          </v-btn>
        </v-toolbar>
      </template> -->

      <!-- Individual column templates -->
      <template v-slot:item.name="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-text-field
            v-model="editedOffense.name"
            dense
            hide-details
            placeholder="Name"
          />
        </template>
        <template v-else>
          {{ item.name }}
        </template>
      </template>

      <template v-slot:item.start="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-text-field
            v-model.number="editedOffense.start"
            type="number"
            dense
            hide-details
            placeholder="Start"
          />
        </template>
        <template v-else>
          {{ item.start }}
        </template>
      </template>

      <template v-slot:item.end="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-text-field
            v-model.number="editedOffense.end"
            type="number"
            dense
            hide-details
            placeholder="End"
          />
        </template>
        <template v-else>
          {{ item.end }}
        </template>
      </template>

      <template v-slot:item.unit="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-select
            v-model="editedOffense.unit"
            :items="timeUnits"
            dense
            hide-details
          />
        </template>
        <template v-else>
          {{ item.unit }}
        </template>
      </template>

      <template v-slot:item.description="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-text-field
            v-model="editedOffense.description"
            dense
            hide-details
            placeholder="Description"
          />
        </template>
        <template v-else>
          {{ item.description }}
        </template>
      </template>

      <template v-slot:item.actions="{ item }">
        <template v-if="editedOffense.id === item.id">
          <v-icon
            small
            class="mr-2"
            @click="saveOffenseType"
          >
            mdi-check
          </v-icon>
          <v-icon
            small
            @click="cancelOffenseEdit"
          >
            mdi-close
          </v-icon>
        </template>
        <template v-else>
          <v-icon
            small
            class="mr-2"
            @click="editOffenseType(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            @click="deleteOffenseType(item)"
          >
            mdi-delete
          </v-icon>
          <v-icon
            small
            class="ml-2"
            @click="openTemplateEditor(item)"
          >
            mdi-file-document-edit
          </v-icon>
        </template>
      </template>

      <!-- Expanded content template -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-card flat>
            <v-card-title>
              Penalties for {{ item.name }}
              <v-spacer />
              <v-btn
                color="primary"
                small
                :disabled="isNewPenaltyRowOpen"
                @click="addNewPenaltyRow(item.id)"
              >
                Add Penalty
              </v-btn>
            </v-card-title>

            <v-data-table
              :headers="penaltyHeaders"
              :items="getPenaltiesForOffense(item.id)"
              hide-default-footer
              dense
            >
              <!-- Individual column templates for penalties -->
              <template v-slot:item.offenseCount="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id">
                  <v-text-field
                    v-model.number="editedPenalty.offenseCount"
                    type="number"
                    dense
                    hide-details
                  />
                </template>
                <template v-else>
                  {{ penalty.offenseCount }}
                </template>
              </template>

              <template v-slot:item.penaltyType="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id">
                  <v-select
                    v-model="editedPenalty.penaltyType"
                    :items="penaltyTypes"
                    dense
                    hide-details
                  />
                </template>
                <template v-else>
                  {{ penalty.penaltyType }}
                </template>
              </template>

              <template v-slot:item.noticeTypeId="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id && hasNoticeComponent(editedPenalty.penaltyType)">
                  <v-select
                    v-model="editedPenalty.noticeTypeId"
                    :items="noticeTypes"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                  />
                </template>
                <template v-else-if="hasNoticeComponent(penalty.penaltyType)">
                  {{ getNoticeTypeName(penalty.noticeTypeId) }}
                </template>
              </template>

              <template v-slot:item.penaltyDays="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id">
                  <v-text-field
                    v-model.number="editedPenalty.penaltyDays"
                    type="number"
                    dense
                    hide-details
                  />
                </template>
                <template v-else>
                  {{ penalty.penaltyDays }}
                </template>
              </template>

              <template v-slot:item.customExpirationMonths="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id">
                  <v-text-field
                    v-model.number="editedPenalty.customExpirationMonths"
                    type="number"
                    dense
                    hide-details
                  />
                </template>
                <template v-else>
                  {{ penalty.customExpirationMonths }}
                </template>
              </template>

              <template v-slot:item.actions="{ item: penalty }">
                <template v-if="editedPenalty.id === penalty.id">
                  <v-icon
                    small
                    class="mr-2"
                    @click="savePenalty"
                  >
                    mdi-check
                  </v-icon>
                  <v-icon
                    small
                    @click="cancelPenaltyEdit"
                  >
                    mdi-close
                  </v-icon>
                </template>
                <template v-else>
                  <v-icon
                    small
                    class="mr-2"
                    @click="editPenalty(penalty)"
                  >
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    small
                    @click="deletePenalty(penalty)"
                  >
                    mdi-delete
                  </v-icon>
                  <v-icon
                    small
                    class="ml-2"
                    @click="openPenaltyTemplateEditor(penalty)"
                  >
                    mdi-file-document-edit
                  </v-icon>
                </template>
              </template>
            </v-data-table>
          </v-card>
        </td>
      </template>
    </v-data-table>

    <!-- Offense Type Template Editor Dialog -->
    <v-dialog
      v-model="templateDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Edit Offense Type Template</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <tiptap-vuetify
              v-model="editedTemplate"
              :extensions="extensions"
              :toolbar-attributes="{ color: 'black' }"
              :card-props="{ flat: true }"
              placeholder="Write your template content..."
            />
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="closeTemplateEditor"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveTemplate"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Penalty Template Editor Dialog -->
    <v-dialog
      v-model="penaltyTemplateDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Edit Penalty Template</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <tiptap-vuetify
              v-model="editedPenaltyTemplate"
              :extensions="extensions"
              :toolbar-attributes="{ color: 'black' }"
              :card-props="{ flat: true }"
              placeholder="Write your template content..."
            />
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="closePenaltyTemplateEditor"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="savePenaltyTemplate"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
  import {
    Q_GET_OFFENSE_TYPES,
    M_CREATE_OFFENSE_TYPE,
    M_UPDATE_OFFENSE_TYPE,
    M_DELETE_OFFENSE_TYPE,
  } from '@/gql/offenseType'
  import {
    Q_GET_PENALTIES,
    M_CREATE_PENALTY,
    M_UPDATE_PENALTY,
    M_DELETE_PENALTY,
  } from '@/gql/penalty'
  import { Q_GET_NOTICE_TYPES } from '@/gql/noticeType'
  import { TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History } from 'tiptap-vuetify'

  export default {
    name: 'OffenseTypes',
    components: { TiptapVuetify },

    data: () => ({
      headers: [
        { text: '', value: 'data-table-expand' },
        { text: 'Name', value: 'name' },
        { text: 'Start', value: 'start' },
        { text: 'End', value: 'end' },
        { text: 'Unit', value: 'unit' },
        { text: 'Description', value: 'description' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      penaltyHeaders: [
        { text: 'Offense Count', value: 'offenseCount' },
        { text: 'Penalty Type', value: 'penaltyType' },
        { text: 'Notice Type', value: 'noticeTypeId' },
        { text: 'Penalty Days', value: 'penaltyDays' },
        { text: 'Expiration (Months)', value: 'customExpirationMonths' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      offenseTypes: [],
      penalties: [],
      expanded: [],
      templateDialog: false,
      penaltyTemplateDialog: false,
      editedTemplate: '',
      editedPenaltyTemplate: '',
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3]
          }
        }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak
      ],
      editedOffense: {
        id: null,
        name: '',
        start: null,
        end: null,
        unit: '',
        description: '',
        template: '',
      },
      defaultOffense: {
        id: null,
        name: '',
        start: null,
        end: null,
        unit: '',
        description: '',
        template: '',
      },
      editedPenalty: {
        id: null,
        offenseTypeId: null,
        offenseCount: null,
        penaltyType: '',
        penaltyDays: null,
        customExpirationMonths: null,
        noticeTypeId: null,
        template: '',
      },
      defaultPenalty: {
        id: null,
        offenseTypeId: null,
        offenseCount: null,
        penaltyType: '',
        penaltyDays: null,
        customExpirationMonths: null,
        noticeTypeId: null,
        template: '',
      },
      timeUnits: ['Minute', 'Day'],
      penaltyTypes: ['Notice', 'Deduction', 'NoticeAndDeduction'],
      noticeTypes: [],
    }),

    computed: {
      isNewOffenseRowOpen () {
        return this.editedOffense.id === 0
      },
      isNewPenaltyRowOpen () {
        return this.editedPenalty.id === 0
      },
    },

    created () {
      this.fetchData()
    },

    methods: {
      async fetchData () {
        try {
          const [offenseResponse, penaltyResponse, noticeResponse] = await Promise.all([
            this.$apollo.query({ query: Q_GET_OFFENSE_TYPES }),
            this.$apollo.query({ query: Q_GET_PENALTIES }),
            this.$apollo.query({ query: Q_GET_NOTICE_TYPES }),
          ])
          this.offenseTypes = offenseResponse.data.offenseTypes
          this.penalties = penaltyResponse.data.penalties
          this.noticeTypes = noticeResponse.data.noticeTypes
        } catch (error) {
          console.error('Error fetching data:', error)
        }
      },

      getPenaltiesForOffense (offenseTypeId) {
        return this.penalties.filter(p => p.offenseTypeId === offenseTypeId)
      },

      // Offense Type Methods
      addNewOffenseRow () {
        this.editedOffense = { ...this.defaultOffense, id: 0 }
        this.offenseTypes.unshift(this.editedOffense)
      },

      editOffenseType (item) {
        this.editedOffense = { ...item }
      },

      async saveOffenseType () {
        try {
          if (this.editedOffense.id === 0) {
            // Create new offense type
            const { data } = await this.$apollo.mutate({
              mutation: M_CREATE_OFFENSE_TYPE,
              variables: {
                name: this.editedOffense.name,
                start: this.editedOffense.start,
                end: this.editedOffense.end,
                unit: this.editedOffense.unit,
                description: this.editedOffense.description,
              },
            })
            this.offenseTypes = this.offenseTypes.filter(item => item.id !== 0)
            this.offenseTypes.push(data.createOffenseType)
          } else {
            // Update existing offense type
            const { data } = await this.$apollo.mutate({
              mutation: M_UPDATE_OFFENSE_TYPE,
              variables: {
                id: this.editedOffense.id,
                name: this.editedOffense.name,
                start: this.editedOffense.start,
                end: this.editedOffense.end,
                unit: this.editedOffense.unit,
                description: this.editedOffense.description,
              },
            })
            const index = this.offenseTypes.findIndex(item => item.id === this.editedOffense.id)
            this.offenseTypes.splice(index, 1, data.updateOffenseType)
          }
          this.editedOffense = { ...this.defaultOffense }
        } catch (error) {
          console.error('Error saving offense type:', error)
        }
      },

      cancelOffenseEdit () {
        if (this.editedOffense.id === 0) {
          this.offenseTypes = this.offenseTypes.filter(item => item.id !== 0)
        }
        this.editedOffense = { ...this.defaultOffense }
      },

      // Penalty Methods
      addNewPenaltyRow (offenseTypeId) {
        this.editedPenalty = {
          ...this.defaultPenalty,
          id: 0,
          offenseTypeId,
          offenseCount: 1,
          penaltyType: this.penaltyTypes[0],
          penaltyDays: 0,
          customExpirationMonths: 0,
          noticeTypeId: null,
        }
        this.penalties.unshift(this.editedPenalty)
      },

      editPenalty (item) {
        this.editedPenalty = { ...item }
      },

      async savePenalty () {
        try {
          const variables = {
            offenseTypeId: this.editedPenalty.offenseTypeId,
            offenseCount: this.editedPenalty.offenseCount,
            penaltyType: this.editedPenalty.penaltyType,
            penaltyDays: this.editedPenalty.penaltyDays,
            ...(this.editedPenalty.customExpirationMonths > 0 && {
              customExpirationMonths: this.editedPenalty.customExpirationMonths,
            }),
          }

          if (this.hasNoticeComponent(this.editedPenalty.penaltyType)) {
            variables.noticeTypeId = this.editedPenalty.noticeTypeId
          }

          if (this.editedPenalty.id === 0) {
            // Create new penalty
            const { data } = await this.$apollo.mutate({
              mutation: M_CREATE_PENALTY,
              variables,
            })
            this.penalties = this.penalties.filter(item => item.id !== 0)
            this.penalties.push(data.createPenalty)
          } else {
            // Update existing penalty
            variables.id = this.editedPenalty.id
            const { data } = await this.$apollo.mutate({
              mutation: M_UPDATE_PENALTY,
              variables,
            })
            const index = this.penalties.findIndex(item => item.id === this.editedPenalty.id)
            this.penalties.splice(index, 1, data.updatePenalty)
          }
          this.editedPenalty = { ...this.defaultPenalty }
        } catch (error) {
          console.error('Error saving penalty:', error)
        }
      },

      cancelPenaltyEdit () {
        if (this.editedPenalty.id === 0) {
          this.penalties = this.penalties.filter(item => item.id !== 0)
        }
        this.editedPenalty = { ...this.defaultPenalty }
      },

      async deleteOffenseType (item) {
        if (confirm('Are you sure you want to delete this offense type?')) {
          try {
            await this.$apollo.mutate({
              mutation: M_DELETE_OFFENSE_TYPE,
              variables: {
                where: { id: item.id },
              },
            })
            this.offenseTypes = this.offenseTypes.filter(type => type.id !== item.id)
          } catch (error) {
            console.error('Error deleting offense type:', error)
          }
        }
      },

      async deletePenalty (item) {
        if (confirm('Are you sure you want to delete this penalty?')) {
          try {
            await this.$apollo.mutate({
              mutation: M_DELETE_PENALTY,
              variables: {
                where: { id: item.id },
              },
            })
            this.penalties = this.penalties.filter(penalty => penalty.id !== item.id)
          } catch (error) {
            console.error('Error deleting penalty:', error)
          }
        }
      },

      hasNoticeComponent(penaltyType) {
        return penaltyType === 'Notice' || penaltyType === 'NoticeAndDeduction'
      },

      getNoticeTypeName(noticeTypeId) {
        const noticeType = this.noticeTypes.find(nt => nt.id === noticeTypeId)
        return noticeType ? noticeType.name : ''
      },

      openTemplateEditor (item) {
        this.editedOffense = { ...item }
        this.editedTemplate = item.template || ''
        this.templateDialog = true
      },

      closeTemplateEditor () {
        this.templateDialog = false
        this.editedTemplate = ''
        this.editedOffense = { ...this.defaultOffense }
      },

      async saveTemplate () {
        try {
          const { data } = await this.$apollo.mutate({
            mutation: M_UPDATE_OFFENSE_TYPE,
            variables: {
              id: this.editedOffense.id,
              template: this.editedTemplate,
            },
          })
          
          const index = this.offenseTypes.findIndex(item => item.id === this.editedOffense.id)
          this.offenseTypes.splice(index, 1, data.updateOffenseType)
          
          this.closeTemplateEditor()
          this.$toast.success('Template updated successfully')
        } catch (error) {
          console.error('Error saving template:', error)
          this.$toast.error('Error updating template')
        }
      },

      openPenaltyTemplateEditor (item) {
        this.editedPenalty = { ...item }
        this.editedPenaltyTemplate = item.template || ''
        this.penaltyTemplateDialog = true
      },

      closePenaltyTemplateEditor () {
        this.penaltyTemplateDialog = false
        this.editedPenaltyTemplate = ''
        this.editedPenalty = { ...this.defaultPenalty }
      },

      async savePenaltyTemplate () {
        try {
          const { data } = await this.$apollo.mutate({
            mutation: M_UPDATE_PENALTY,
            variables: {
              id: this.editedPenalty.id,
              template: this.editedPenaltyTemplate,
            },
          })
          
          const index = this.penalties.findIndex(item => item.id === this.editedPenalty.id)
          this.penalties.splice(index, 1, data.updatePenalty)
          
          this.closePenaltyTemplateEditor()
          this.$toast.success('Penalty template updated successfully')
        } catch (error) {
          console.error('Error saving penalty template:', error)
          this.$toast.error('Error updating penalty template')
        }
      },
    },
  }
</script>
